import axios from 'axios';
import { 
  LoginRequest, 
  RegisterRequest, 
  AuthResponse, 
  AnonymousSession,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ChangeEmailRequest,
  DeleteAccountRequest,
  SubscriptionInfo,
  GoogleCodeExchangeRequest,
  MicrosoftCodeExchangeRequest
} from '../types/auth';

// Use relative URLs in development (with proxy) and full URLs in production
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? '' // Use proxy in development
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000');

const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies
});

// Add request interceptor to automatically include Authorization header for authenticated requests
authApi.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Create a separate axios instance for token refresh to avoid circular references
const refreshApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add response interceptor to handle token refresh
authApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is 401 and we haven't already tried to refresh the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Use the separate refresh API instance to avoid circular references
        // Refresh token is stored as an HTTP-only cookie, so we don't need to send it in the body
        const refreshResponse = await refreshApi.post('/api/auth/refresh');
        
        if (refreshResponse.data.success && refreshResponse.data.accessToken) {
          
          // Update the access token in localStorage
          localStorage.setItem('accessToken', refreshResponse.data.accessToken);
          
          // Update the Authorization header for the original request
          originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.accessToken}`;
          
          // Retry the original request with the new token
          return authApi(originalRequest);
        } else {
          throw new Error('Token refresh response invalid');
        }
      } catch (refreshError) {
        console.error('Token refresh error:', refreshError);
        // Token refresh failed, clear the access token and redirect to login
        localStorage.removeItem('accessToken');
        window.location.href = '/auth';
      }
    }
    
    return Promise.reject(error);
  }
);

export class AuthAPI {
  // User registration
  static async register(request: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.post<AuthResponse>('/api/auth/register', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to register user'
      };
    }
  }

  // User login
  static async login(request: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.post<AuthResponse>('/api/auth/login', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to login'
      };
    }
  }

  static async exchangeGoogleCode(request: GoogleCodeExchangeRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.post<AuthResponse>('/api/auth/google/code', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to sign in with Google'
      };
    }
  }

  static async exchangeMicrosoftCode(request: MicrosoftCodeExchangeRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.post<AuthResponse>('/api/auth/microsoft/code', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to sign in with Microsoft'
      };
    }
  }

  // User logout
  static async logout(): Promise<AuthResponse> {
    try {
      const response = await authApi.post<AuthResponse>('/api/auth/logout');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to logout'
      };
    }
  }

  // Refresh access token
  static async refreshToken(): Promise<AuthResponse> {
    try {
      const response = await authApi.post<AuthResponse>('/api/auth/refresh');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to refresh token'
      };
    }
  }

  // Get current user info
  static async getCurrentUser(): Promise<AuthResponse> {
    try {
      const response = await authApi.get<AuthResponse>('/api/auth/me');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to get user info'
      };
    }
  }

  // Create or get anonymous session
  static async getOrCreateAnonymousSession(sessionId?: string, preferences?: any): Promise<AuthResponse> {
    try {
      const response = await authApi.post<AuthResponse>('/api/auth/anonymous-session', {
        sessionId,
        preferences
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to create anonymous session'
      };
    }
  }

  // Update anonymous session preferences
  static async updateAnonymousPreferences(sessionId: string, preferences: any): Promise<AuthResponse> {
    try {
      const response = await authApi.put<AuthResponse>(`/api/auth/anonymous-session/${sessionId}`, {
        preferences
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to update anonymous preferences'
      };
    }
  }

  // Revoke all tokens for current user
  static async revokeAllTokens(): Promise<AuthResponse> {
    try {
      const response = await authApi.post<AuthResponse>('/api/auth/revoke-all-tokens');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to revoke tokens'
      };
    }
  }

  // Update user profile
  static async updateProfile(request: UpdateProfileRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.put<AuthResponse>('/api/auth/profile', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to update profile'
      };
    }
  }

  // Change user password
  static async changePassword(request: ChangePasswordRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.put<AuthResponse>('/api/auth/change-password', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to change password'
      };
    }
  }

  // Change user email
  static async changeEmail(request: ChangeEmailRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.put<AuthResponse>('/api/auth/change-email', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to change email'
      };
    }
  }

  // Get subscription information
  static async getSubscription(): Promise<{ success: boolean; data?: { subscription: SubscriptionInfo }; message?: string }> {
    try {
      const response = await authApi.get<{ success: boolean; data?: { subscription: SubscriptionInfo }; message?: string }>('/api/auth/subscription');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to get subscription info'
      };
    }
  }

  // Delete user account
  static async deleteAccount(request: DeleteAccountRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.delete<AuthResponse>('/api/auth/account', { data: request });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error.message || 'Failed to delete account'
      };
    }
  }
}

export default authApi;
